<template>
  <div class="grid grid-cols-5 gap-2 text-sm m-4">
    <component
      :title="slideoverTitle"
      :is="slideoverComponent"
      :visible="slideoverVisible"
      v-on:toggle-slideover="closeSlideover"
      :orders="selectedRows"
    />

    <!-- Left Column -->
    <div class="flex flex-col col-span-1 space-y-2">
      <!-- Form -->
      <div
        class="max-w-full overflow-hidden border-b border-gray-200 rounded-lg shadow"
      >
        <div
          class="px-6 py-2 text-gray-500 bg-gray-50 border-b border-gray-200"
        >
          Search Orders
        </div>
        <div class="px-6 py-4 space-y-4 text-black bg-white">
          <div class="flex flex-row space-x-4">
            <div class="w-full">
              <form @submit.prevent="searchOrders" class="space-y-4">
                <!-- SKU or VBI Select (Product) -->
                <div>
                  <label
                    :for="`Product-${_uid}`"
                    class="block text-sm font-medium leading-5 text-gray-700"
                  >
                    Product
                  </label>
                  <ProductSelect
                    :reduce-opts="20"
                    :return-product-details="false"
                    @change-handler="loadVariants"
                  />
                </div>

                <!-- VBI Select (Variant) -->
                <div>
                  <label class="block text-sm font-medium leading-5 text-gray-700">
                    Variant
                  </label>
                  <VariantSelect
                    :product-id="productId"
                    @change-handler="selectVariant"
                  />
                </div>

                <!-- Status -->
                <div>
                  <label
                    :for="`search-status-${_uid}`"
                    class="block text-sm font-medium leading-5 text-gray-700"
                    >Status</label
                  >
                  <select
                    :id="`search-status-${_uid}`"
                    v-model="status"
                    class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option selected value="backordered">Backordered</option>
                    <option value="cns">Committed/Not Shipped</option>
                  </select>
                </div>

                <!-- Start Date -->
                <div>
                  <label
                    :for="`search-start-${_uid}`"
                    class="block text-sm font-medium leading-5 text-gray-700"
                  >
                    Start Date:
                  </label>
                  <v-date-picker
                    :id="`search-start-${_uid}`"
                    :popover="{ visibility: 'focus' }"
                    class="block mt-1"
                    color="green"
                    v-model="startDate"
                  />
                </div>

                <!-- End Date -->
                <div>
                  <label
                    :for="`search-end-${_uid}`"
                    class="block text-sm font-medium leading-5 text-gray-700"
                  >
                    End Date:
                  </label>
                  <v-date-picker
                    :id="`search-end-${_uid}`"
                    :popover="{ visibility: 'focus' }"
                    class="block mt-1"
                    color="green"
                    v-model="endDate"
                  />
                </div>

                <div>
                  <Button
                    type="submit"
                    :disabled="processing || !canProcess"
                    class="w-full flex justify-center"
                  >
                    <span>Search</span>
                    <LoadingSpinner class="ml-2" v-if="processing" />
                  </Button>
                </div>

                <div>
                  <Button
                    :disabled="!hasRowsSelected"
                    class="btn-blue w-full flex justify-center"
                    v-on:click-handler="openCreateDiscountSlideover()"
                  >
                    <span>Open Email Slideover</span>
                  </Button>
                </div>
              </form>
            </div>
          </div>

          <HttpError :error="error" dismissable />
        </div>
      </div>
    </div>

    <!-- Right Column -->
    <div class="flex flex-col col-span-4 space-y-2">
      <!-- Results -->
      <div class="flex-grow" v-show="rowData">
        <ag-grid-vue
          style="width: 100%; height: 800px"
          class="ag-theme-balham"
          :columnDefs="columnDefs"
          :rowData="rowData"
          :paginationAutoPageSize="false"
          :paginationPageSize="paginationPageSize"
          :pagination="true"
          id="order_tracking"
          :gridOptions="gridOptions"
          @grid-ready="onGridReady"
          :context="context"
          :frameworkComponents="frameworkComponents"
          :defaultColDef="defaultColDef"
          :enableCellTextSelection="true"
          :ensureDomOrder="true"
          @selection-changed="onSelectionChanged"
        >
        </ag-grid-vue>
      </div>
    </div>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button.vue";
import { datetime } from "@/components/agGrid/cellRenderers";
import CreateTicketsSlideover from "../components/slideovers/CreateTickets.vue";
import ProductSelect from "@/components/elements/VBI/ProductSelect";
import VariantSelect from "@/components/elements/VBI/VariantSelect";

const Utilities = ApiFactory.get("utilities");

export default {
  name: "SearchOrders",
  components: {
    Button,
    AgGridVue,
    CreateTicketsSlideover,
    ProductSelect,
    VariantSelect
  },
  data() {
    return {
      skus: [],
      sku: null,
      skuLoading: true,
      status: "backordered",
      startDate: null,
      endDate: null,
      results: [],
      error: null,
      processing: false,

      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      selectedRows: [],

      slideoverComponent: null,
      slideoverTitle: null,
      slideoverVisible: false,
      showCreatedDiscount: false,

      productId: null,
      variantId: null,
    };
  },
  computed: {
    canProcess() {
        return this.productId !== null && this.variantId !== null && this.startDate && this.endDate;
    },
    hasRowsSelected() {
      return this.selectedRows.length;
    },
    filteredSkus() {
      return this.skus
        .filter((inventorySku) =>
          inventorySku.toLowerCase().includes(String(this.sku).toLowerCase())
        )
        .slice(0, 20);
    },
  },
  created() {},
  methods: {
    loadVariants(productId) {
      this.productId = productId;
    },
    selectVariant(variantId) {
      this.variantId = variantId;
    },
    onGridReady() {},
    onSelectionChanged(event) {
      this.selectedRows = event.api.getSelectedRows();
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    searchOrders() {
      this.error = null;
      this.processing = true;

      let params = {
        status: this.status,
        start_date: this.startDate,
        end_date: this.endDate,
        variant_id: this.variantId
      };

      Utilities.bulkEmailSearch(params)
        .then((response) => {
          this.rowData = response.data.body;
          this.gridApi.sizeColumnsToFit();
        })
        .catch((error) => (this.error = error))
        .finally(() => (this.processing = false));
    },
    openCreateDiscountSlideover() {
      this.slideoverComponent = "CreateTicketsSlideover";
      this.slideoverTitle = "Send Bulk Emails";

      setTimeout(() => {
        this.slideoverVisible = true;
      }, 5);
    },
    closeSlideover() {
      this.slideoverVisible = false;

      setTimeout(() => {
        this.slideoverComponent = null;
      }, 500);
    },
  },
  async beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 30,
      pagination: false,
      suppressPaginationPanel: true,
      rowSelection: "multiple",
      onSelectionChanged: this.onNoteSelectionChanged,
      rowDeselection: true,
    };

    this.context = { componentParent: this };
    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 0,
      suppressMovable: true,
    };

    this.columnDefs = [
      {
        headerName: "Order Date",
        field: "OrderDate",
        width: 250,
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      {
        headerName: "Variant",
        valueGetter: (params) => {
          return params.data.variant_id;
        },
      },
      {
        headerName: "Product",
        width: 400,
        valueGetter: (params) => {
          return params.data.variant.product.name;
        },
        tooltipValueGetter: (params) => {
          return params.data.variant.product.name;
        }
      },
      {
        headerName: "Size",
        valueGetter: (params) => {
          return params.data.variant.variant_name;
        }
      },
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        width: 250,
      },
      {
        headerName: "Site",
        valueGetter: (params) => {
          if ([2, 22].includes(parseInt(params.data.CartID))) {
            return "FGT";
          }

          if ([23].includes(parseInt(params.data.CartID))) {
            return "BB";
          }

          if ([31].includes(parseInt(params.data.CartID))) {
            return "PT";
          }

          if ([32].includes(parseInt(params.data.CartID))) {
            return "Trees";
          }

          return "--";
        },
        width: 130,
      },
      {
        headerName: "QTY/Needed",
        width: 250,
        valueGetter: (params) =>
          params.data.QuantityNeeded + "/" + params.data.QuantityOrdered,
      },
      {
        headerName: "ESD",
        field: "ExpectedShipDate",
      },
      {
        headerName: "Email",
        field: "Email",
        width: 400,
      },
    ];

    this.frameworkComponents = {
      dateCell: datetime,
    };

  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.rowData = null;
  },
};
</script>
<style scoped lang="scss">
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
